import React from 'react';

import { useIntl } from 'react-intl-next';

import { captureException } from '@atlaskit/linking-common/sentry';

import { type CreateFlags } from '../../common/types';
import { useAnalyticsEvents } from '../../common/utils/analytics/generated/use-analytics-events';

import { BaseErrorBoundary } from './error-boundary-base';
import { messages } from './messages';

type EditorExtensionLinkCreateErrorBoundaryProps = {
	children: React.ReactNode;
	flags?: CreateFlags;
	onError?: () => void;
};

const NullFallback = () => null;

const getErrorType = (error: unknown) => {
	if (error instanceof Response) {
		return 'NetworkError';
	}
	if (error instanceof Error) {
		return error.name;
	}
	return typeof error;
};

export const ErrorBoundary = ({
	flags,
	children,
	onError,
}: EditorExtensionLinkCreateErrorBoundaryProps): React.JSX.Element => {
	const { fireEvent } = useAnalyticsEvents();
	const intl = useIntl();

	return (
		<BaseErrorBoundary
			errorComponent={<NullFallback />}
			onError={(error) => {
				// Capture exception to Sentry
				if (error instanceof Error) {
					captureException(error, 'editor-extension-link-create');
				}

				flags?.showFailure?.({
					title: intl.formatMessage(messages.title),
					description: intl.formatMessage(messages.description),
				});

				fireEvent('operational.editorExtensionLinkCreateExperience.failed', {
					errorType: getErrorType(error),
				});

				onError?.();
			}}
		>
			{children}
		</BaseErrorBoundary>
	);
};
